import React from "react";
import { Box, ButtonBase, ThemeProvider, Typography, alpha, darken, lighten } from "@mui/material";
import { darkSurfaceMuiTheme } from "../../Theme";
import { ArrowBackIosNewRounded } from "@mui/icons-material";
import AudioManager from "../../@shared/utils/AudioManager";
import {
  colors,
  displayTextSx,
  eyebrowTextSx,
  fonts,
  glassChipSx,
  glassPanelSx,
  gradients,
  radii,
  shadows,
} from "../../@shared/ui/tokens";

const playTap = () => AudioManager.playSFX("buttonPress");

export function GlassPanel({ children, sx, ...rest }) {
  return (
    <Box sx={{ ...glassPanelSx, ...sx }} {...rest}>
      {children}
    </Box>
  );
}

// Square glass button for icon-only actions (back, sort, filter, close).
export function GlassIconButton({
  icon,
  label,
  onClick,
  size = 44,
  active = false,
  sx,
  ...rest
}) {
  return (
    <ButtonBase
      aria-label={label}
      title={label}
      onClick={(event) => {
        playTap();
        onClick?.(event);
      }}
      sx={{
        ...glassChipSx,
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: `${radii.md}px`,
        color: active ? colors.goldBright : colors.text,
        borderColor: active ? "rgba(255, 211, 106, 0.55)" : colors.lineStrong,
        transition: "transform 140ms ease, background-color 140ms ease, border-color 140ms ease",
        "& svg": { fontSize: Math.round(size * 0.48) },
        "&:hover": {
          backgroundColor: colors.surfaceHover,
          transform: "translateY(-1px)",
        },
        "&:active": { transform: "translateY(0) scale(0.97)" },
        "&.Mui-focusVisible": {
          outline: `2px solid ${colors.gold}`,
          outlineOffset: 2,
        },
        ...sx,
      }}
      {...rest}
    >
      {icon}
    </ButtonBase>
  );
}

// Full-width top bar used by menu screens (Cards, Decks, Builder, Store...).
export function ScreenHeader({
  title,
  eyebrow,
  onBack,
  backLabel = "Back",
  right,
  children,
  sx,
}) {
  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        zIndex: 5,
        width: "100%",
        boxSizing: "border-box",
        flexShrink: 0,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1.5, md: 3 },
        py: { xs: 1, md: 1.5 },
        background:
          "linear-gradient(180deg, rgba(10, 5, 24, 0.92) 0%, rgba(10, 5, 24, 0.7) 100%)",
        borderBottom: `1px solid ${colors.lineStrong}`,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: -1,
          height: "1px",
          background: gradients.goldLine,
          opacity: 0.7,
        },
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {onBack && (
          <GlassIconButton
            label={backLabel}
            onClick={onBack}
            size={40}
            icon={<ArrowBackIosNewRounded />}
          />
        )}
      </Box>

      <Box sx={{ textAlign: "center", minWidth: 0 }}>
        {eyebrow && (
          <Typography sx={{ ...eyebrowTextSx, color: colors.gold, lineHeight: 1.2 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography
          component="h1"
          sx={{
            ...displayTextSx,
            fontSize: { xs: "1.6rem", md: "2.15rem" },
            lineHeight: 1.05,
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1,
          minWidth: 0,
        }}
      >
        {right}
      </Box>
    </Box>
  );
}

// Pill tab switcher (Infinity / Characters, etc.).
export function SegmentedControl({ options, value, onChange, size = "md", sx }) {
  const compact = size === "sm";
  return (
    <Box
      role="tablist"
      sx={{
        ...glassChipSx,
        display: "inline-flex",
        p: 0.5,
        gap: 0.5,
        borderRadius: `${radii.pill}px`,
        ...sx,
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            role="tab"
            aria-selected={selected}
            data-driver={option.dataDriver}
            onClick={() => {
              if (selected) return;
              playTap();
              onChange(option.value);
            }}
            sx={{
              px: compact ? 1.5 : 2.25,
              height: compact ? 32 : 38,
              borderRadius: `${radii.pill}px`,
              gap: 0.75,
              fontFamily: fonts.body,
              fontWeight: 800,
              fontSize: compact ? "0.72rem" : "0.8rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              color: selected ? "#2a1600" : colors.textSoft,
              background: selected ? gradients.gold : "transparent",
              boxShadow: selected ? `${shadows.glowGold}, inset 0 1px 0 rgba(255,255,255,0.6)` : "none",
              transition: "color 160ms ease, background 160ms ease",
              "&:hover": {
                color: selected ? "#2a1600" : colors.text,
                backgroundColor: selected ? undefined : "rgba(255,255,255,0.06)",
              },
            }}
          >
            {option.icon}
            {option.label}
            {option.count != null && (
              <Box
                component="span"
                sx={{
                  ml: 0.25,
                  px: 0.75,
                  py: 0.1,
                  borderRadius: `${radii.pill}px`,
                  fontSize: "0.68rem",
                  background: selected ? "rgba(42, 22, 0, 0.18)" : "rgba(255,255,255,0.1)",
                }}
              >
                {option.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// Small stat readout: label above a big number.
export function StatChip({ label, value, accent = colors.gold, icon, sx }) {
  return (
    <Box
      sx={{
        ...glassChipSx,
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: `${radii.md}px`,
        ...sx,
      }}
    >
      {icon}
      <Box sx={{ lineHeight: 1 }}>
        <Typography sx={{ ...eyebrowTextSx, fontSize: "0.6rem" }}>{label}</Typography>
        <Typography
          sx={{
            fontFamily: fonts.display,
            fontSize: "1.15rem",
            color: accent,
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// Scope dark-mode MUI defaults (inputs, labels, paper) to a subtree.
export function DarkSurface({ children }) {
  return <ThemeProvider theme={darkSurfaceMuiTheme}>{children}</ThemeProvider>;
}

// Compact primary action for ScreenHeader's right slot (Build, Save, Decks).
export function HeaderActionButton({ label, icon, onClick, disabled, tone = "violet", sx, ...rest }) {
  const background =
    tone === "gold" ? gradients.gold : tone === "play" ? gradients.play : gradients.violet;
  return (
    <ButtonBase
      disabled={disabled}
      onClick={(event) => {
        playTap();
        onClick?.(event);
      }}
      sx={{
        height: 40,
        px: { xs: 1.5, md: 2.25 },
        gap: 0.75,
        borderRadius: `${radii.md}px`,
        fontFamily: fonts.display,
        fontSize: { xs: "1rem", md: "1.15rem" },
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
        color: tone === "gold" ? "#3a1f00" : "#fff",
        background,
        border: "1px solid rgba(255,255,255,0.4)",
        boxShadow: `0 8px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)`,
        transition: "transform 150ms ease, filter 150ms ease",
        "& svg": { fontSize: 20 },
        "&:hover": { transform: "translateY(-1px)", filter: "brightness(1.08)" },
        "&.Mui-disabled": { opacity: 0.45, filter: "grayscale(0.6)" },
        ...sx,
      }}
      {...rest}
    >
      {icon}
      {label}
    </ButtonBase>
  );
}

// Compact in-match action button. Accepts NeoButton-style props (text, icon,
// startAdorn, endAdorn, color) so it can stand in for NeoButton in action bars.
export function ActionButton({
  text,
  children,
  icon,
  startAdorn,
  endAdorn,
  color = colors.violet,
  onClick,
  disabled,
  dataDriver,
  size = "md",
}) {
  const compact = size === "sm";
  const base = String(color).startsWith("#") ? color : colors.violet;
  const top = lighten(base, 0.22);
  const bottom = darken(base, 0.28);
  const label = text || children;
  return (
    <ButtonBase
      data-driver={dataDriver}
      disabled={disabled}
      aria-label={typeof label === "string" ? label : undefined}
      onClick={(event) => {
        if (disabled) return;
        onClick?.(event);
        playTap();
      }}
      sx={{
        width: "100%",
        minHeight: compact ? { xs: 36, md: 40 } : 48,
        px: compact ? 0.75 : 1.5,
        gap: compact ? 0.5 : 0.75,
        borderRadius: `${compact ? radii.md : radii.md + 2}px`,
        fontFamily: fonts.display,
        fontSize: compact
          ? { xs: "0.85rem", md: "1rem" }
          : { xs: "1.05rem", md: "1.15rem" },
        whiteSpace: "nowrap",
        letterSpacing: "0.04em",
        lineHeight: 1,
        color: "#fff",
        textShadow: "0 1px 2px rgba(0,0,0,0.45)",
        background: `linear-gradient(180deg, ${top} 0%, ${base} 50%, ${bottom} 100%)`,
        border: `1px solid ${alpha(lighten(base, 0.5), 0.7)}`,
        boxShadow: `0 8px 20px ${alpha(base, 0.4)}, inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -3px 0 rgba(0,0,0,0.2)`,
        transition: "transform 140ms ease, filter 140ms ease",
        "& img": {
          marginRight: "0 !important",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5)) !important",
          ...(compact
            ? { width: "20px !important", height: "20px !important", display: { xs: "none", sm: "inline" } }
            : {}),
        },
        "& svg": { fontSize: 22 },
        "&:hover": { transform: "translateY(-1px)", filter: "brightness(1.08)" },
        "&:active": { transform: "scale(0.97)" },
        "&.Mui-disabled": { opacity: 0.45, filter: "grayscale(0.5)" },
        "&.Mui-focusVisible": { outline: `2px solid ${colors.gold}`, outlineOffset: 2 },
      }}
    >
      {startAdorn}
      {icon}
      {label && <span>{label}</span>}
      {endAdorn}
    </ButtonBase>
  );
}
